import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { metadataFillLayout } from 'features/knowledgeDocumentViewer/components/metadata/metadataFillLayout'
import { PageMetadataEvalChrome } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalChrome'
import { usePageMetadataEvalSession } from 'features/knowledgeDocumentViewer/contexts/PageMetadataEvalSessionContext'
import { useAppendDocumentPageMetadataUserEditComment } from 'features/knowledgeDocumentViewer/hooks/useAppendDocumentPageMetadataUserEditComment'
import { usePageMetadataEvalActions } from 'features/knowledgeDocumentViewer/hooks/usePageMetadataEvalActions'
import { usePageMetadataEvalForm } from 'features/knowledgeDocumentViewer/hooks/usePageMetadataEvalForm'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { pageMetadataEvalHistoryForUser } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalHistory'
import { useGeckoPermissions } from 'features/permissions'
import React, { useCallback, useMemo, useState } from 'react'

export type PageMetadataEvalSectionProps = {
  pageEntry: SystemDocumentPageEntry | undefined
  pageNum: number
  isLoading: boolean
  bqDocumentId: string | undefined
  pageUserEdits: readonly DocumentPageMetadataUserEditEntry[]
  deferReason?: string
}

export function PageMetadataEvalSection({
  pageEntry,
  pageNum,
  isLoading,
  bqDocumentId,
  pageUserEdits,
  deferReason,
}: PageMetadataEvalSectionProps): React.ReactElement {
  const { t } = useLingui()
  const {
    canGoBack,
    canGoForward,
    currentPage,
    isSampling,
    error,
    retry,
    runExclusive,
    next,
    previous,
    pageIsOnScreen,
  } = usePageMetadataEvalSession()
  const pageStatus = currentPage?.decision ?? 'unlabeled'
  const labeledBy = currentPage?.labeled_by?.trim() || undefined
  const { user } = useGeckoPermissions()
  const [commentText, setCommentText] = useState('')
  const { latestEvent: evalEvent, comments } = useMemo(
    () => pageMetadataEvalHistoryForUser(pageUserEdits, user?.email ?? ''),
    [pageUserEdits, user?.email]
  )
  const canAddComment = evalEvent !== undefined
  const {
    editableFields,
    values,
    generatedValuesAreEmpty,
    shouldHaveMetadata,
    canConfirm,
    setFieldValue,
    setShouldHaveMetadata,
  } = usePageMetadataEvalForm({
    pageEntry,
    pageNum,
    savedEvalEvent: evalEvent,
    mixInValues:
      currentPage?.blind_qa === true
        ? (currentPage.values ?? undefined)
        : undefined,
  })
  const { isSaving, confirmPage, deferPage } = usePageMetadataEvalActions({
    bqDocumentId,
    pageNum,
    pageEntry,
  })
  const { isAppending, appendComment } =
    useAppendDocumentPageMetadataUserEditComment()

  const handleConfirm = useCallback(() => {
    void confirmPage(values, shouldHaveMetadata, commentText).then(
      () => setCommentText(''),
      () => undefined
    )
  }, [commentText, confirmPage, shouldHaveMetadata, values])

  const handleDefer = useCallback(
    async (reason: string) => {
      await deferPage(reason, values, shouldHaveMetadata, commentText)
      setCommentText('')
    },
    [commentText, deferPage, shouldHaveMetadata, values]
  )

  const handleAddComment = useCallback(() => {
    void appendComment({
      host: evalEvent,
      text: commentText,
      bqDocumentId,
    }).then((added) => {
      if (added) setCommentText('')
    })
  }, [appendComment, bqDocumentId, commentText, evalEvent])

  const handleNext = useCallback(() => {
    void runExclusive(() => next())
  }, [next, runExclusive])

  const handlePrevious = useCallback(() => {
    previous()
  }, [previous])

  const handleRetry = useCallback(() => {
    void retry()
  }, [retry])

  return (
    <Box display='flex' direction='column' gap='md' {...metadataFillLayout}>
      {error ? (
        <Box display='flex' direction='column' gap='sm'>
          <Text color='destructive'>{error.message}</Text>
          <Button onClick={handleRetry} disabled={isSampling}>
            {t`Retry loading pages`}
          </Button>
        </Box>
      ) : null}
      <PageMetadataEvalChrome
        isLoading={isLoading}
        isSaving={isSaving || isAppending}
        isSampling={isSampling}
        canConfirm={canConfirm}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        pageStatus={pageStatus}
        generatedValuesAreEmpty={generatedValuesAreEmpty}
        shouldHaveMetadata={shouldHaveMetadata}
        editableFields={editableFields}
        onFieldChange={setFieldValue}
        onShouldHaveMetadataChange={setShouldHaveMetadata}
        comments={comments}
        commentText={commentText}
        canAddComment={canAddComment}
        onCommentTextChange={setCommentText}
        onAddComment={handleAddComment}
        onConfirm={handleConfirm}
        onDefer={handleDefer}
        onPrevious={handlePrevious}
        onNext={handleNext}
        deferReason={deferReason}
        labeledBy={labeledBy}
        pageIsOnScreen={pageIsOnScreen}
      />
    </Box>
  )
}
